import { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Alert, Pressable, StyleSheet, Platform, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { authApi } from '../../shared/utils/api';
import { Contact, ContactType, CreateContactRequest, UpdateContactRequest } from '../../shared/types/contact';
import { ContactFormModal } from '../../components/contacts';
import { Card, Button, Input } from '../../components/ui';
import { addColorOpacity } from '../../shared/utils/styles';
import { PageContainer, PageHeader } from '../../components/layout';
import { logger } from '../../shared/utils/logger';
import { Ionicons } from '@expo/vector-icons';
import { useToast } from '../../components/toast';

// Contact type categories for better organization
const TYPE_CATEGORIES = {
  'Financial': ['lender', 'banker', 'accountant', 'insurance_agent'],
  'Legal & Title': ['attorney', 'title_company'],
  'Property Services': ['real_estate_agent', 'property_manager', 'appraiser', 'inspector'],
  'Land & Survey': ['surveyor', 'engineer', 'environmental', 'soil_lab'],
  'Utilities & Systems': ['utility_company', 'septic_service', 'well_service'],
  'Farm & Land': ['forester', 'farmer_rancher', 'veterinarian', 'pest_control'],
  'Construction': ['contractor'],
  'People': ['neighbor', 'government'],
  'Other': ['vendor', 'other'],
};

// Icon mapping for contact types
const TYPE_ICONS: Record<string, string> = {
  lender: 'card-outline',
  banker: 'briefcase-outline',
  contractor: 'hammer-outline',
  real_estate_agent: 'home-outline',
  inspector: 'search-outline',
  attorney: 'scale-outline',
  accountant: 'calculator-outline',
  insurance_agent: 'shield-outline',
  property_manager: 'business-outline',
  vendor: 'cube-outline',
  other: 'person-outline',
  surveyor: 'compass-outline',
  title_company: 'document-text-outline',
  septic_service: 'water-outline',
  well_service: 'rainy-outline',
  soil_lab: 'layers-outline',
  environmental: 'leaf-outline',
  forester: 'flower-outline',
  appraiser: 'trending-up-outline',
  engineer: 'construct-outline',
  utility_company: 'flash-outline',
  government: 'business-outline',
  neighbor: 'people-outline',
  farmer_rancher: 'nutrition-outline',
  veterinarian: 'medkit-outline',
  pest_control: 'bug-outline',
};

export default function ContactsPage() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const styles = useStyles();
  const responsive = useResponsive();
  const { isMobile, isSmallMobile, isTablet, isDesktop, isLargeDesktop } = responsive;
  const { tokens, utils } = styles;
  const toast = useToast();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const pageStyles = makePageStyles(tokens, utils, isMobile, isSmallMobile, isTablet, isDesktop);

  useEffect(() => {
    loadContactTypes();
    loadContacts();
  }, []);

  const loadContactTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await authApi.getContactTypes();
      setContactTypes(response.types);
    } catch (error: any) {
      logger.error('Failed to load contact types', error, 'contacts');
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await authApi.getUserContacts();
      setContacts(response.contacts);
    } catch (error: any) {
      logger.error('Failed to load contacts', error, 'contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async (data: CreateContactRequest) => {
    await authApi.createUserContact(data);
    await loadContacts();
  };

  const handleUpdateContact = async (contactId: number, data: UpdateContactRequest) => {
    await authApi.updateUserContact(contactId, data);
    await loadContacts();
  };

  const handleDeleteContact = async (contact: Contact) => {
    const displayName = contact.company_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'this contact';
    
    Alert.alert('Delete Contact', `Are you sure you want to delete ${displayName}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await authApi.deleteUserContact(contact.id);
            await loadContacts();
            toast.showSuccess('Contact deleted');
          } catch (error: any) {
            toast.showError(error.message || 'Failed to delete contact');
          }
        },
      },
    ]);
  };

  // Filter contacts
  const filteredContacts = useMemo(() => {
    return contacts.filter((contact) => {
      // Search filter
      const matchesSearch = !searchQuery || 
        contact.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.notes?.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory = !selectedCategory || 
        Object.entries(TYPE_CATEGORIES).some(([cat, types]) => 
          cat === selectedCategory && types.includes(contact.contact_type.name)
        );

      // Type filter
      const matchesType = !selectedType || contact.contact_type.name === selectedType;

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [contacts, searchQuery, selectedCategory, selectedType]);

  // Group contacts by type for organized display
  const groupedContacts = useMemo(() => {
    const groups: Record<string, Contact[]> = {};
    filteredContacts.forEach(contact => {
      const typeName = contact.contact_type.name;
      if (!groups[typeName]) groups[typeName] = [];
      groups[typeName].push(contact);
    });
    return groups;
  }, [filteredContacts]);

  // Get available categories based on actual contacts
  const availableCategories = useMemo(() => {
    const types = new Set(contacts.map(c => c.contact_type.name));
    return Object.entries(TYPE_CATEGORIES)
      .filter(([_, catTypes]) => catTypes.some(t => types.has(t)))
      .map(([cat]) => cat);
  }, [contacts]);

  // Grid columns based on screen size
  const gridColumns = isLargeDesktop ? 4 : isDesktop ? 3 : isTablet ? 2 : 1;

  const handleCall = (phone: string) => {
    Linking.openURL(`tel:${phone}`);
  };

  const handleEmail = (email: string) => {
    Linking.openURL(`mailto:${email}`);
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.showSuccess(`${label} copied to clipboard`);
      }
    } catch (e) {
      toast.showError('Failed to copy');
    }
  };

  const renderContactCard = (contact: Contact) => {
    const firstName = contact.first_name || '';
    const lastName = contact.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || contact.company_name || 'Unnamed';
    const icon = TYPE_ICONS[contact.contact_type.name] || 'person-outline';

    return (
      <Card key={contact.id} style={pageStyles.contactCard} padding={isMobile ? 'md' : 'lg'}>
        {/* Header */}
        <View style={pageStyles.cardHeader}>
          <View style={pageStyles.cardIcon}>
            <Ionicons name={icon as any} size={20} color={tokens.color.primary} />
          </View>
          <View style={pageStyles.cardHeaderActions}>
            <Pressable
              onPress={() => {
                setEditingContact(contact);
                setFormModalVisible(true);
              }}
              style={pageStyles.iconButton}
            >
              <Ionicons name="pencil-outline" size={16} color={tokens.color.onSurface} />
            </Pressable>
            <Pressable
              onPress={() => handleDeleteContact(contact)}
              style={[pageStyles.iconButton, pageStyles.deleteButton]}
            >
              <Ionicons name="trash-outline" size={16} color={tokens.color.error} />
            </Pressable>
          </View>
        </View>

        {/* Name & Company */}
        <Text style={pageStyles.contactName} numberOfLines={1}>{fullName}</Text>
        {contact.company_name && firstName && (
          <Text style={pageStyles.companyName} numberOfLines={1}>{contact.company_name}</Text>
        )}
        {contact.job_title && (
          <Text style={pageStyles.jobTitle} numberOfLines={1}>{contact.job_title}</Text>
        )}

        {/* Type Badge */}
        <View style={pageStyles.typeBadge}>
          <Text style={pageStyles.typeBadgeText}>
            {contact.contact_type.name.replace(/_/g, ' ')}
          </Text>
        </View>

        {/* Contact Actions */}
        <View style={pageStyles.contactActions}>
          {contact.phone && (
            <Pressable 
              style={pageStyles.actionButton}
              onPress={() => handleCall(contact.phone!)}
              onLongPress={() => handleCopy(contact.phone!, 'Phone')}
            >
              <Ionicons name="call-outline" size={16} color={tokens.color.success} />
              <Text style={pageStyles.actionText} numberOfLines={1}>{contact.phone}</Text>
            </Pressable>
          )}
          {contact.mobile && contact.mobile !== contact.phone && (
            <Pressable 
              style={pageStyles.actionButton}
              onPress={() => handleCall(contact.mobile!)}
              onLongPress={() => handleCopy(contact.mobile!, 'Mobile')}
            >
              <Ionicons name="phone-portrait-outline" size={16} color={tokens.color.success} />
              <Text style={pageStyles.actionText} numberOfLines={1}>{contact.mobile}</Text>
            </Pressable>
          )}
          {contact.email && (
            <Pressable 
              style={pageStyles.actionButton}
              onPress={() => handleEmail(contact.email!)}
              onLongPress={() => handleCopy(contact.email!, 'Email')}
            >
              <Ionicons name="mail-outline" size={16} color={tokens.color.primary} />
              <Text style={pageStyles.actionText} numberOfLines={1}>{contact.email}</Text>
            </Pressable>
          )}
        </View>

        {/* Notes Preview */}
        {contact.notes && (
          <Text style={pageStyles.notesPreview} numberOfLines={2}>
            {contact.notes}
          </Text>
        )}
      </Card>
    );
  };

  return (
    <PageContainer verticalPadding={isMobile ? 'lg' : '2xl'}>
      <PageHeader
        title="Address Book"
        subtitle={`${contacts.length} professional contact${contacts.length !== 1 ? 's' : ''}`}
      />

      {/* Search & Filters */}
      <Card style={pageStyles.filtersCard} padding={isMobile ? 'md' : 'lg'}>
        {/* Search Input */}
        <View style={pageStyles.searchRow}>
          <View style={pageStyles.searchInputContainer}>
            <Ionicons name="search-outline" size={18} color={addColorOpacity(tokens.color.onSurface, 0.5)} style={pageStyles.searchIcon} />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              style={pageStyles.searchInput}
            />
          </View>
          <Button
            title={isMobile ? "+" : "+ Add Contact"}
            onPress={() => {
              setEditingContact(null);
              setFormModalVisible(true);
            }}
            variant="primary"
            size={isMobile ? 'sm' : 'md'}
          />
        </View>

        {/* Category Filters */}
        {availableCategories.length > 0 && (
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={pageStyles.filterScroll}
            contentContainerStyle={pageStyles.filterScrollContent}
          >
            <Pressable
              onPress={() => { setSelectedCategory(''); setSelectedType(''); }}
              style={[
                pageStyles.filterChip,
                !selectedCategory && pageStyles.filterChipActive
              ]}
            >
              <Text style={[
                pageStyles.filterChipText,
                !selectedCategory && pageStyles.filterChipTextActive
              ]}>
                All ({contacts.length})
              </Text>
            </Pressable>
            {availableCategories.map(category => {
              const categoryTypes = TYPE_CATEGORIES[category as keyof typeof TYPE_CATEGORIES] || [];
              const count = contacts.filter(c => categoryTypes.includes(c.contact_type.name)).length;
              return (
                <Pressable
                  key={category}
                  onPress={() => { 
                    setSelectedCategory(selectedCategory === category ? '' : category); 
                    setSelectedType(''); 
                  }}
                  style={[
                    pageStyles.filterChip,
                    selectedCategory === category && pageStyles.filterChipActive
                  ]}
                >
                  <Text style={[
                    pageStyles.filterChipText,
                    selectedCategory === category && pageStyles.filterChipTextActive
                  ]}>
                    {category} ({count})
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        )}

        {/* View Toggle (desktop only) */}
        {!isMobile && (
          <View style={pageStyles.viewToggle}>
            <Pressable
              onPress={() => setViewMode('grid')}
              style={[pageStyles.viewButton, viewMode === 'grid' && pageStyles.viewButtonActive]}
            >
              <Ionicons name="grid-outline" size={18} color={viewMode === 'grid' ? tokens.color.primary : tokens.color.onSurface} />
            </Pressable>
            <Pressable
              onPress={() => setViewMode('list')}
              style={[pageStyles.viewButton, viewMode === 'list' && pageStyles.viewButtonActive]}
            >
              <Ionicons name="list-outline" size={18} color={viewMode === 'list' ? tokens.color.primary : tokens.color.onSurface} />
            </Pressable>
          </View>
        )}
      </Card>

      {/* Contacts Grid/List */}
      {loading || loadingTypes ? (
        <View style={pageStyles.loadingContainer}>
          <ActivityIndicator size="large" color={tokens.color.primary} />
          <Text style={pageStyles.loadingText}>Loading contacts...</Text>
        </View>
      ) : filteredContacts.length === 0 ? (
        <View style={pageStyles.emptyContainer}>
          <Ionicons name="people-outline" size={48} color={addColorOpacity(tokens.color.onSurface, 0.3)} />
          <Text style={pageStyles.emptyTitle}>
            {searchQuery || selectedCategory ? 'No matches found' : 'No contacts yet'}
          </Text>
          <Text style={pageStyles.emptySubtitle}>
            {searchQuery || selectedCategory 
              ? 'Try adjusting your search or filters'
              : 'Add your first contact to get started'}
          </Text>
          {!searchQuery && !selectedCategory && (
            <Button
              title="+ Add Contact"
              onPress={() => {
                setEditingContact(null);
                setFormModalVisible(true);
              }}
              variant="primary"
              style={{ marginTop: utils.spacing.lg }}
            />
          )}
        </View>
      ) : (
        <View style={[
          pageStyles.contactsGrid,
          { 
            flexDirection: viewMode === 'list' || isMobile ? 'column' : 'row',
            flexWrap: viewMode === 'grid' && !isMobile ? 'wrap' : 'nowrap',
          }
        ]}>
          {filteredContacts.map(contact => (
            <View 
              key={contact.id} 
              style={[
                pageStyles.cardWrapper,
                viewMode === 'grid' && !isMobile && { 
                  width: `${100 / gridColumns - 2}%`,
                  marginRight: '1%',
                  marginLeft: '1%',
                }
              ]}
            >
              {renderContactCard(contact)}
            </View>
          ))}
        </View>
      )}

      {/* Form Modal */}
      <ContactFormModal
        visible={formModalVisible}
        onClose={() => {
          setFormModalVisible(false);
          setEditingContact(null);
        }}
        onSubmit={async (data) => {
          if (editingContact) {
            await handleUpdateContact(editingContact.id, data as UpdateContactRequest);
          } else {
            await handleCreateContact(data as CreateContactRequest);
          }
        }}
        contact={editingContact}
        contactTypes={contactTypes}
      />
    </PageContainer>
  );
}

function makePageStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean,
  isTablet: boolean,
  isDesktop: boolean
) {
  return StyleSheet.create({
    filtersCard: {
      marginBottom: utils.spacing.lg,
    },
    searchRow: {
      flexDirection: 'row',
      gap: utils.spacing.md,
      alignItems: 'center',
    },
    searchInputContainer: {
      flex: 1,
      position: 'relative',
    },
    searchIcon: {
      position: 'absolute',
      left: utils.spacing.md,
      top: '50%',
      transform: [{ translateY: -9 }],
      zIndex: 1,
    },
    searchInput: {
      paddingLeft: utils.spacing.xl + utils.spacing.md,
    },
    filterScroll: {
      marginTop: utils.spacing.md,
    },
    filterScrollContent: {
      gap: utils.spacing.sm,
      paddingRight: utils.spacing.lg,
    },
    filterChip: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.full,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.surface,
    },
    filterChipActive: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    filterChipText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.medium,
    },
    filterChipTextActive: {
      color: tokens.color.onPrimary,
    },
    viewToggle: {
      flexDirection: 'row',
      position: 'absolute',
      right: utils.spacing.lg,
      bottom: utils.spacing.lg,
      gap: utils.spacing.xs,
    },
    viewButton: {
      padding: utils.spacing.xs,
      borderRadius: utils.radius.sm,
    },
    viewButtonActive: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: utils.spacing['2xl'],
    },
    loadingText: {
      marginTop: utils.spacing.md,
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: utils.spacing['2xl'],
    },
    emptyTitle: {
      marginTop: utils.spacing.md,
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    emptySubtitle: {
      marginTop: utils.spacing.xs,
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
    contactsGrid: {
      gap: utils.spacing.md,
    },
    cardWrapper: {
      marginBottom: isMobile ? 0 : utils.spacing.md,
    },
    contactCard: {
      marginBottom: isMobile ? utils.spacing.md : 0,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: utils.spacing.sm,
    },
    cardIcon: {
      width: 36,
      height: 36,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardHeaderActions: {
      flexDirection: 'row',
      gap: utils.spacing.xs,
    },
    iconButton: {
      width: 32,
      height: 32,
      borderRadius: utils.radius.sm,
      backgroundColor: tokens.color.surfaceElevated,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    deleteButton: {
      backgroundColor: addColorOpacity(tokens.color.error, 0.1),
    },
    contactName: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      marginBottom: 2,
    },
    companyName: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      fontFamily: utils.typography.body.fontFamily,
    },
    jobTitle: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginTop: 2,
    },
    typeBadge: {
      alignSelf: 'flex-start',
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: 2,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.secondary, 0.1),
      marginTop: utils.spacing.sm,
    },
    typeBadgeText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.secondary,
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'capitalize',
    },
    contactActions: {
      marginTop: utils.spacing.md,
      gap: utils.spacing.xs,
    },
    actionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    actionText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    notesPreview: {
      marginTop: utils.spacing.md,
      paddingTop: utils.spacing.md,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      fontStyle: 'italic',
      lineHeight: utils.typography.sizes.xs * 1.5,
    },
  });
}
